/**
 * Resizable — les panneaux qu'on redimensionne (`react-resizable-panels`, habillé sur les jetons ;
 * retapé au lot 5 du catalogue, 02-11-2026). Partagé par les panneaux Lab (issus de DozLab) et
 * par tout écran maître-détail.
 *
 * La POIGNÉE : une petite pilule à peine visible au repos (l'encre à 14 %), qui passe à la MARQUE
 * — avec sa lueur — quand on la survole, qu'on la prend ou qu'on y arrive au clavier : la marque
 * sur ce qu'on manipule, jamais ailleurs. La zone de prise fait 10 px, la pilule 3.
 *
 *   <ResizablePanelGroup direction="horizontal">
 *     <ResizablePanel defaultSize={40}>…</ResizablePanel>
 *     <ResizableHandle />
 *     <ResizablePanel>…</ResizablePanel>
 *   </ResizablePanelGroup>
 */
import { forwardRef } from "react";
import {
  Panel,
  PanelGroup,
  PanelResizeHandle,
  type ImperativePanelHandle,
  type PanelGroupProps,
  type PanelProps,
  type PanelResizeHandleProps,
} from "react-resizable-panels";

import { cn } from "./utils";
import "./surfaces.css";

export type { ImperativePanelHandle } from "react-resizable-panels";

export function ResizablePanelGroup({ className, ...props }: PanelGroupProps) {
  return <PanelGroup {...props} className={cn("min-h-0 min-w-0 flex-1", className)} />;
}

const PANEL_FLEX_STYLE = {
  display: "flex" as const,
  flexDirection: "column" as const,
};

export const ResizablePanel = forwardRef<ImperativePanelHandle, PanelProps>(function ResizablePanel({ style, ...props }, ref) {
  // Panel rend un seul div flex-grow sans `display: flex` interne : on le force, pour qu'un
  // enfant unique en `flex-1 min-h-0` remplisse le panneau.
  return <Panel ref={ref} {...props} style={{ ...PANEL_FLEX_STYLE, ...style }} />;
});

export function ResizableHandle({ className, ...props }: PanelResizeHandleProps) {
  return (
    <PanelResizeHandle
      {...props}
      className={cn(
        "group relative flex shrink-0 items-center justify-center outline-none",
        "data-[panel-group-direction=horizontal]:w-2.5 data-[panel-group-direction=horizontal]:cursor-col-resize",
        "data-[panel-group-direction=vertical]:h-2.5 data-[panel-group-direction=vertical]:cursor-row-resize",
        className,
      )}
    >
      <span
        className={cn(
          "rz-poignee sf-rond pointer-events-none",
          "group-data-[panel-group-direction=horizontal]:h-8 group-data-[panel-group-direction=horizontal]:w-[3px]",
          "group-data-[panel-group-direction=vertical]:h-[3px] group-data-[panel-group-direction=vertical]:w-8",
        )}
      />
    </PanelResizeHandle>
  );
}
